import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoBaseModal } from "@redotech/arbiter/modal/redo-base-modal";
import { Text } from "@redotech/arbiter/text";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { memo, ReactNode } from "react";
import * as allFiltersModalCss from "./all-filters-modal.module.css";

interface AllFiltersModalProps {
  "data-dd-action-name"?: string;
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  onResetFilters?: () => void;
  children: ReactNode;
  addFilterButton?: ReactNode;
}

export const AllFiltersModal = memo(function AllFiltersModal({
  open,
  onClose,
  onConfirm,
  onResetFilters,
  children,
  addFilterButton,
  "data-dd-action-name": actionName,
}: AllFiltersModalProps) {
  const { t } = useTranslation();
  const handleConfirm = useHandler(() => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  });

  const handleClose = useHandler(() => {
    onClose();
  });

  const handleResetFilters = useHandler(() => {
    if (onResetFilters) {
      onResetFilters();
    }
  });

  return (
    <RedoBaseModal
      data-dd-action-name={actionName}
      disableEnforceFocus
      isOpen={open}
      onModalCloseRequested={handleClose}
      otherClasses={[allFiltersModalCss.modal]}
    >
      <>
        <Flex
          as="header"
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          dir="column"
          gap="md"
          p="3xl"
          shrink={0}
        >
          <Text fontSize="lg" fontWeight="semibold" textColor="primary">
            {t("All filters", { context: "filter modal" })}
          </Text>
          <Flex w="fit-content">{addFilterButton}</Flex>
        </Flex>
        <Flex
          as="section"
          bgColor="secondary"
          flex={1}
          overflowY="auto"
          p="none"
        >
          <Flex dir="row" gap="sm" p="xl" wrap="wrap">
            {children}
          </Flex>
        </Flex>
        <Flex
          align="center"
          as="footer"
          borderColor="primary"
          borderStyle="solid"
          borderTopWidth="1px"
          dir="row"
          justify="flex-end"
          p="3xl"
          shrink={0}
        >
          <Flex dir="row" gap="md">
            {onResetFilters && (
              <RedoButton
                data-dd-action-name={actionName}
                hierarchy="tertiary"
                onClick={handleResetFilters}
                size="sm"
                text={t("Reset filters", { context: "filters" })}
              />
            )}
            <RedoButton
              data-dd-action-name={actionName}
              hierarchy="secondary"
              onClick={handleClose}
              size="sm"
              text={t("Done", { context: "filter modal" })}
            />
            {onConfirm && (
              <RedoButton
                data-dd-action-name={actionName}
                hierarchy="primary"
                onClick={handleConfirm}
                size="sm"
                text={t("Confirm", { context: "filter modal" })}
              />
            )}
          </Flex>
        </Flex>
      </>
    </RedoBaseModal>
  );
});
